import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./components/app";

const sentryEnabled = !window.origin.includes("localhost");

// Preview apps live on *.ondigitalocean.app; only the real domain is production. Without this
// every preview reported its errors as production.
const sentryEnvironment = /(^|\.)asmaskiner\.se$/.test(window.location.hostname)
  ? "production"
  : "preview";

Sentry.init({
  dsn: "https://b8415a87942338eeaab1d9905451cded@o4506959758688256.ingest.us.sentry.io/4506959763865600",
  environment: sentryEnvironment,
  integrations: [
    Sentry.captureConsoleIntegration({
      levels: ["error"],
    }),
    Sentry.browserTracingIntegration(),
  ],
  enabled: sentryEnabled,
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/asmaskiner\.se\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.001, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

/**
 * Session replay is one of the largest pieces of the Sentry SDK and it records for a fraction
 * of a percent of sessions, so it is fetched from Sentry's CDN after the app has mounted
 * instead of shipping in the bundle. Error capture and tracing are configured above and work
 * whether or not this ever resolves — a blocked CDN just means no replays.
 */
async function loadSessionReplay() {
  if (!sentryEnabled) return;

  try {
    const replayIntegration = await Sentry.lazyLoadIntegration("replayIntegration");
    Sentry.addIntegration(
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      })
    );
  } catch {
    // Blocked or offline — nothing to do, the rest of Sentry is already running.
  }
}

/**
 * A hydration mismatch means the server-rendered DOM and the first client render disagreed.
 * React recovers by re-rendering that subtree from scratch — silently, and in production
 * without telling you which node it was. That re-render is exactly what this build is trying
 * to avoid (it throws away the server paint and is what made the LCP element appear late), so
 * every occurrence is reported instead of being absorbed.
 */
function onRecoverableError(error, errorInfo) {
  // biome-ignore lint/suspicious/noConsole: the only signal a mismatch leaves in the browser
  console.error("[hydration] React recovered from an error while hydrating:", error);

  Sentry.captureException(error, {
    tags: { hydration: "mismatch" },
    extra: { componentStack: errorInfo?.componentStack },
  });
}

const container = document.getElementById("root");

const tree = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

/**
 * The server renders public pages into #root (server.js + server/page.js), so the markup is
 * already there and React only has to adopt it. Everything else — /admin, unknown paths, and
 * any request where the SSR bundle was missing or the render threw — is served the plain SPA
 * shell with an empty #root, and an empty root must be rendered, not hydrated: hydrateRoot on
 * an empty container reports the whole app as a mismatch.
 */
if (container.hasChildNodes()) {
  ReactDOM.hydrateRoot(container, tree, { onRecoverableError });
} else {
  ReactDOM.createRoot(container).render(tree);
}

loadSessionReplay();
